/**
 * CSV・顧客設定サービス
 * 
 * バックエンドのCallable Functionsを呼び出して設定を管理する
 */

import { httpsCallable } from "firebase/functions";
import { getFunctionsInstance } from "../lib/firebase";
import { SerializedTimestamp } from "../types/common";
// import { 
//   CsvTemplate 
// } from "../../../functions/src/lib/csv/types";

// 型定義はバックエンドと共有（必要に応じてフロントエンド用に再定義するか、importで共有）
// ここでは簡易的にバックエンドの型定義をインポートする前提ですが、
// 実際にはフロントエンドで型定義を持つのが一般的です。
// 今回はビルド設定によっては直接インポートできない可能性があるため、必要なインターフェースを再定義します。

// --- 型定義 (フロントエンド用) ---

export type CsvEncoding = "utf-8" | "shift_jis";

export interface CsvColumn {
  order: number;
  headerName: string;
  jsonPath: string;
  defaultValue?: string;
  required: boolean;
  transform?: string;
}

export interface CsvTemplateCommon {
  id: string;
  name: string;
  description: string;
  columns: CsvColumn[];
  encoding: CsvEncoding;
  promptAddition?: string;
  createdAt: SerializedTimestamp | Date;
  updatedAt: SerializedTimestamp | Date;
}

export interface CsvSchema {
  templateId?: string;
  columns: CsvColumn[];
  encoding: CsvEncoding;
}

export interface OcrConfig {
  promptAddition?: string;
}

export interface EmailConfig {
  replyTo: string[];
  ccAddresses?: string[];
  subjectTemplate?: string;
  bodyTemplate?: string;
}

export interface CustomerSettings {
  csvSchema?: CsvSchema;
  ocrConfig?: OcrConfig;
  emailConfig?: EmailConfig;
}

/**
 * 伝票種別ごとの個別設定
 */
export interface SlipTypeConfig {
  /** 種別固有の追加プロンプト */
  promptAddition?: string;
  /** 種別固有のCSV設定 */
  csvSchema?: CsvSchema;
  /** エイリアス（別名）リスト */
  aliases?: string[];
}

// --- API レスポンス型 ---

interface TemplatesResponse {
  templates: CsvTemplateCommon[];
}

interface TemplateIdResponse {
  templateId: string;
}

interface SettingsResponse {
  settings: CustomerSettings;
}

// --- テンプレート操作入力型 ---

export interface CreateCsvTemplateInput {
  name: string;
  description: string;
  columns: CsvColumn[];
  encoding: CsvEncoding;
  promptAddition?: string;
}

export interface UpdateCsvTemplateInput {
  name?: string;
  description?: string;
  columns?: CsvColumn[];
  encoding?: CsvEncoding;
  promptAddition?: string;
}

// --- サービス関数 ---

/**
 * CSVテンプレート一覧を取得
 */
export async function fetchCsvTemplates(): Promise<CsvTemplateCommon[]> {
  const functions = getFunctionsInstance();
  const getCsvTemplates = httpsCallable<void, TemplatesResponse>(functions, "getCsvTemplates");
  const result = await getCsvTemplates();
  return result.data.templates;
}

/**
 * CSVテンプレートを作成
 */
export async function createCsvTemplate(input: CreateCsvTemplateInput): Promise<string> {
  const functions = getFunctionsInstance();
  const createCsvTemplateFn = httpsCallable<CreateCsvTemplateInput, TemplateIdResponse>(functions, "createCsvTemplate");
  const result = await createCsvTemplateFn(input);
  return result.data.templateId;
}

/**
 * CSVテンプレートを更新
 */
export async function updateCsvTemplate(templateId: string, updates: UpdateCsvTemplateInput): Promise<void> {
  const functions = getFunctionsInstance();
  const updateCsvTemplateFn = httpsCallable<{ templateId: string } & UpdateCsvTemplateInput, { success: boolean }>(functions, "updateCsvTemplate");
  await updateCsvTemplateFn({ templateId, ...updates });
}

/**
 * CSVテンプレートを削除
 */
export async function deleteCsvTemplate(templateId: string): Promise<void> {
  const functions = getFunctionsInstance();
  const deleteCsvTemplateFn = httpsCallable(functions, "deleteCsvTemplate");
  await deleteCsvTemplateFn({ templateId });
}

/**
 * 顧客の全設定を取得
 */
export async function fetchCustomerSettings(customerId: string): Promise<CustomerSettings> {
  const functions = getFunctionsInstance();
  const getCustomerAllSettings = httpsCallable<{ customerId: string }, SettingsResponse>(functions, "getCustomerAllSettings");
  const result = await getCustomerAllSettings({ customerId });
  return result.data.settings;
}

/**
 * 顧客のCSV設定を保存
 */
export async function saveCustomerCsvSchema(customerId: string, input: CsvSchema): Promise<void> {
  const functions = getFunctionsInstance();
  const setCustomerCsvSchema = httpsCallable<{ customerId: string } & CsvSchema, { success: boolean }>(functions, "setCustomerCsvSchema");
  await setCustomerCsvSchema({ customerId, ...input });
}

/**
 * テンプレートを顧客に適用
 */
export async function applyTemplateToCustomer(customerId: string, templateId: string): Promise<void> {
  const functions = getFunctionsInstance();
  const applyTemplateToCustomerFn = httpsCallable(functions, "applyTemplateToCustomer");
  await applyTemplateToCustomerFn({ customerId, templateId });
}

/**
 * 顧客のOCR設定を保存
 */
export async function saveCustomerOcrConfig(customerId: string, input: OcrConfig): Promise<void> {
  const functions = getFunctionsInstance();
  const setCustomerOcrConfig = httpsCallable<{ customerId: string } & OcrConfig, { success: boolean }>(functions, "setCustomerOcrConfig");
  await setCustomerOcrConfig({ customerId, ...input });
}

/**
 * 顧客のメール設定を保存
 */
export async function saveCustomerEmailConfig(customerId: string, input: EmailConfig): Promise<void> {
  const functions = getFunctionsInstance();
  const setCustomerEmailConfig = httpsCallable<{ customerId: string } & EmailConfig, { success: boolean }>(functions, "setCustomerEmailConfig");
  await setCustomerEmailConfig({ customerId, ...input });
}
